<template>
  <div class="ciw">
    <ProductMarketWidget v-if="isMarket" :item-id="itemId" :section="marketSection" />
    <ContentSkeleton v-else-if="loading && !data" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!source" class="dw-empty">{{ t("noSources") }}</p>

    <!-- Resumen: operaciones, desde cuándo, con cuántos compradores y proveedores, cuánto. -->
    <template v-else-if="view === 'summary'">
      <div class="ciw-kpis">
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ sourceLabel(source) }}<template v-if="kinds.length > 1"> · {{ t(`kinds.${side}`) }}</template></span>
          <span class="ciw-kpi__value">{{ opCount }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("first") }}</span>
          <span class="ciw-kpi__value">{{ dateText(summary.first) }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("last") }}</span>
          <span class="ciw-kpi__value">{{ dateText(summary.last) }}</span>
        </div>
        <div v-if="summary.buyers" class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("buyersCount") }}</span>
          <span class="ciw-kpi__value">{{ summary.buyers }}</span>
        </div>
        <div v-if="summary.sellers" class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("sellersCount") }}</span>
          <span class="ciw-kpi__value">{{ summary.sellers }}</span>
        </div>
        <div v-for="q in summary.qty" :key="'q-' + q.unit" class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("qty") }} · {{ unitLabel(q.unit) }}</span>
          <span class="ciw-kpi__value">{{ money(q.qty) }}</span>
        </div>
        <div v-for="v in summary.totals" :key="'t-' + (v.currency || 'none')" class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("total") }} · {{ cur(v.currency) }}</span>
          <span class="ciw-kpi__value">{{ money(v.amount) }}</span>
        </div>
      </div>
    </template>

    <!-- Últimas operaciones: con quién, cuánto y por cuánto. -->
    <template v-else-if="view === 'operations'">
      <p v-if="!ops.length" class="dw-empty">{{ t("noOperations") }}</p>
      <ul v-else class="ciw-list">
        <li v-for="o in recentOps" :key="o.id + o.kind" class="ciw-row">
          <span class="ciw-row__date">{{ shortDate(o.date) }}</span>
          <div class="ciw-row__body">
            <router-link :to="`/dynamic-model/${o.model}/view/${o.id}`" class="ciw-row__title">{{ o.name || "—" }}</router-link>
            <span class="ciw-row__text">{{ partiesText(o) }} · {{ qtyText(o) }}</span>
          </div>
          <span v-if="amountOf(o) != null" class="ciw-row__amount">{{ money(amountOf(o)) }} <small>{{ cur(o.currency) }}</small></span>
        </li>
      </ul>
      <p v-if="ops.length > recentOps.length" class="ciw-note">{{ t("showingLast", { n: recentOps.length, total: ops.length }) }}</p>
    </template>

    <!-- Gráficos -->
    <template v-else>
      <div v-if="caption" class="ciw-caption">{{ caption }}</div>
      <p v-if="!points.length" class="dw-empty">{{ t("noData") }}</p>
      <div v-else class="ciw-chart">
        <WidgetChart :type="chartType" :points="points" />
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Un widget de la ficha del producto. Todos menos el de mercado reciben la misma
 * respuesta de /api/product-insights/:id (la carga la grilla una vez) y la agrupan
 * según `view`. Cada uno muestra una fuente (contratos, embarques o proformas) y un
 * lado (venta o compra; en los contratos de corretaje van juntos): mezclarlos contaría
 * dos veces lo mismo. Cantidades por unidad y montos por moneda, nunca sumados entre sí.
 */
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import WidgetChart from "@/components/gotrading/dashboard/widgets/WidgetChart.vue";
import { CHART_TYPES } from "@/components/gotrading/dashboard/widgetData";
import insightWidgetMixin, { MONTH_OPTIONS, LIMIT_OPTIONS } from "./insightWidgetMixin";
import ProductMarketWidget, { MARKET_SECTIONS } from "./ProductMarketWidget.vue";
import { loadMarket } from "./productMarketStore";

const LIST_LIMIT = 30;

export default {
  name: "ProductInsightWidget",
  components: { ContentSkeleton, WidgetChart, ProductMarketWidget },
  mixins: [insightWidgetMixin],
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    itemId: { type: String, default: "" },
  },
  computed: {
    view() {
      return this.entry.view;
    },
    /** Widgets de mercado: «market» (el resumen) o «market:<sección>». */
    isMarket() {
      return this.view === "market" || String(this.view).startsWith("market:");
    },
    marketSection() {
      const s = String(this.view).split(":")[1];
      return MARKET_SECTIONS.includes(s) ? s : "summary";
    },
    sources() {
      return (this.data && this.data.sources) || [];
    },
    source() {
      const keys = this.sources.map((s) => s.key);
      if (this.config.source && keys.includes(this.config.source)) return this.config.source;
      return keys[0] || null;
    },
    /** Lados que tiene la fuente con datos: venta, compra o corretaje. */
    kinds() {
      const set = new Set(this.all.filter((e) => e.source === this.source).map((e) => e.kind));
      return ["sale", "purchase", "trade"].filter((k) => set.has(k));
    },
    side() {
      if (this.config.side && this.kinds.includes(this.config.side)) return this.config.side;
      return this.kinds[0] || "sale";
    },
    all() {
      return (this.data && this.data.entries) || [];
    },
    /** Compradores: ventas y corretaje; proveedores: compras y corretaje. El resto, el lado elegido. */
    ops() {
      const src = this.all.filter((e) => e.source === this.source);
      if (this.view === "buyers") return src.filter((e) => e.buyer);
      if (this.view === "sellers") return src.filter((e) => e.seller);
      return src.filter((e) => e.kind === this.side);
    },
    opCount() {
      return new Set(this.ops.map((o) => o.id)).size;
    },
    recentOps() {
      return this.ops.slice(0, LIST_LIMIT);
    },
    measure() {
      return this.config.measure === "operations" ? "operations" : "qty";
    },
    /** Unidades con datos, la más usada primero. */
    unitsAvailable() {
      const count = {};
      this.ops.forEach((o) => o.lines.forEach((l) => (count[l.unit || ""] = (count[l.unit || ""] || 0) + 1)));
      return Object.keys(count).sort((a, b) => count[b] - count[a]);
    },
    unit() {
      const u = this.config.unit;
      if (u !== undefined && this.unitsAvailable.includes(u)) return u;
      return this.unitsAvailable[0] || "";
    },
    usesUnit() {
      return ["byMonth", "buyers", "sellers"].includes(this.view) ? this.measure === "qty" : this.view === "price";
    },
    isTimeView() {
      return ["byMonth", "volume", "price"].includes(this.view);
    },
    isMoneyView() {
      return ["volume", "price"].includes(this.view);
    },
    currencies() {
      if (!this.isMoneyView) return [];
      const uniq = Array.from(new Set(this.ops.filter((o) => o.currency !== undefined).map((o) => o.currency || null)));
      const sys = this.data && this.data.systemCurrency;
      return uniq.sort((a, b) => (a === sys ? -1 : b === sys ? 1 : 0));
    },
    currency() {
      if (this.config.currency !== undefined && this.currencies.includes(this.config.currency)) return this.config.currency;
      return this.currencies.length ? this.currencies[0] : null;
    },
    summary() {
      const dates = this.ops.map((o) => o.date).filter(Boolean).sort();
      const buyers = new Set(this.ops.map((o) => o.buyer).filter(Boolean));
      const sellers = new Set(this.ops.map((o) => o.seller).filter(Boolean));
      const qty = {};
      const totals = {};
      this.ops.forEach((o) => {
        o.lines.forEach((l) => {
          const k = l.unit || "";
          qty[k] = (qty[k] || 0) + l.qty;
          if (l.amount != null) {
            const c = o.currency || "none";
            totals[c] = totals[c] || { currency: o.currency || null, amount: 0 };
            totals[c].amount += l.amount;
          }
        });
      });
      return {
        first: dates[0] || null,
        last: dates[dates.length - 1] || null,
        buyers: buyers.size,
        sellers: sellers.size,
        qty: Object.entries(qty).map(([unit, q]) => ({ unit, qty: q })),
        totals: Object.values(totals),
      };
    },
    points() {
      const month = (o) => (o.date || "").slice(0, 7);
      if (this.view === "byMonth") {
        const rows = this.measure === "qty" ? this.ops.map((o) => ({ month: month(o), value: this.qtyIn(o, this.unit) })) : this.distinct(this.ops).map((o) => ({ month: month(o), value: 1 }));
        return this.perMonth(rows);
      }
      if (this.view === "volume") {
        return this.perMonth(this.ops.filter((o) => (o.currency || null) === this.currency).map((o) => ({ month: month(o), value: this.amountOf(o) || 0 })));
      }
      if (this.view === "price") {
        // Precio promedio del mes: monto / cantidad, en la unidad y la moneda elegidas.
        const acc = {};
        this.ops
          .filter((o) => (o.currency || null) === this.currency)
          .forEach((o) => o.lines.filter((l) => (l.unit || "") === this.unit && l.qty && l.amount != null).forEach((l) => {
            const m = month(o);
            acc[m] = acc[m] || { qty: 0, amount: 0 };
            acc[m].qty += l.qty;
            acc[m].amount += l.amount;
          }));
        const keys = this.monthKeys;
        if (!keys.some((k) => acc[k])) return [];
        // Los meses sin operaciones quedan fuera: un precio cero no existe.
        return keys.filter((k) => acc[k]).map((k) => ({ label: this.monthLabel(k), value: Math.round((acc[k].amount / acc[k].qty) * 100) / 100 }));
      }
      if (this.view === "buyers" || this.view === "sellers") {
        const field = this.view === "buyers" ? "buyer" : "seller";
        const map = {};
        const rows = this.measure === "qty" ? this.ops : this.distinct(this.ops);
        rows.forEach((o) => {
          const v = this.measure === "qty" ? this.qtyIn(o, this.unit) : 1;
          if (!v) return;
          map[o[field]] = (map[o[field]] || 0) + v;
        });
        return this.top(Object.entries(map).map(([c, value]) => ({ label: this.contactName(c), value })));
      }
      return [];
    },
    caption() {
      const parts = [];
      if (this.sources.length > 1) parts.push(this.sourceLabel(this.source));
      if (this.kinds.length > 1 && !["buyers", "sellers"].includes(this.view)) parts.push(this.t(`kinds.${this.side}`));
      if (["byMonth", "buyers", "sellers"].includes(this.view)) parts.push(this.t(`measure.${this.measure}`));
      if (this.usesUnit && this.unit) parts.push(this.unitLabel(this.unit));
      if (this.isMoneyView) parts.push(this.view === "price" ? `${this.cur(this.currency)} / ${this.unitLabel(this.unit)}` : this.cur(this.currency));
      return parts.join(" · ");
    },
    menuItems() {
      if (this.isMarket) return [{ key: "research", label: this.$tn("webProfile.searchAgain"), icon: "sync-alt" }];
      const items = [];
      if (this.entry.chart) {
        items.push({ divider: true, key: "d-chart", label: this.$tn("dashboardWidgets.settings.chartType") });
        CHART_TYPES.forEach((k) => items.push({ key: `chart:${k}`, label: this.$tn(`dashboardWidgets.settings.type.${k}`), active: k === this.chartType }));
      }
      if (this.sources.length > 1) {
        items.push({ divider: true, key: "d-source", label: this.tn("menu.source") });
        this.sources.forEach((s) => items.push({ key: `source:${s.key}`, label: this.tn(`sources.${s.key}`), active: s.key === this.source }));
      }
      if (this.kinds.length > 1 && !["buyers", "sellers"].includes(this.view)) {
        items.push({ divider: true, key: "d-side", label: this.tn("menu.side") });
        this.kinds.forEach((k) => items.push({ key: `side:${k}`, label: this.tn(`kinds.${k}`), active: k === this.side }));
      }
      if (["byMonth", "buyers", "sellers"].includes(this.view)) {
        items.push({ divider: true, key: "d-measure", label: this.tn("menu.measure") });
        ["qty", "operations"].forEach((k) => items.push({ key: `measure:${k}`, label: this.tn(`measure.${k}`), active: k === this.measure }));
      }
      if (this.usesUnit && this.unitsAvailable.length > 1) {
        items.push({ divider: true, key: "d-unit", label: this.tn("menu.unit") });
        this.unitsAvailable.forEach((u) => items.push({ key: `unit:${u}`, label: this.unitLabel(u), active: u === this.unit }));
      }
      if (this.isTimeView) {
        items.push({ divider: true, key: "d-months", label: this.tn("menu.period") });
        MONTH_OPTIONS.forEach((n) => items.push({ key: `months:${n}`, label: n ? this.tn("menu.months", { n }) : this.tn("menu.allTime"), active: n === this.months }));
      }
      if (["buyers", "sellers"].includes(this.view)) {
        items.push({ divider: true, key: "d-limit", label: this.tn("menu.show") });
        LIMIT_OPTIONS.forEach((n) => items.push({ key: `limit:${n}`, label: this.tn("menu.top", { n }), active: n === this.limit }));
      }
      if (this.isMoneyView && this.currencies.length > 1) {
        items.push({ divider: true, key: "d-cur", label: this.$tn("dashboardWidgets.finance.currency") });
        this.currencies.forEach((c) => items.push({ key: `currency:${c || ""}`, label: this.cur(c), active: c === this.currency }));
      }
      return items;
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  methods: {
    t(key, params) {
      return this.$t(`productWidgets.${key}`, params);
    },
    tn(key, params) {
      return this.$tn(`productWidgets.${key}`, params);
    },
    onMenu(key) {
      // Busca de nuevo en la web: se actualizan todos los widgets de mercado del producto.
      if (key === "research") {
        loadMarket(this.itemId, this.$i18n.locale, true);
        return;
      }
      const [name, ...rest] = key.split(":");
      const value = rest.join(":");
      if (["months", "limit"].includes(name)) this.$emit("update-config", { [name]: parseInt(value, 10) });
      else if (name === "currency") this.$emit("update-config", { currency: value || null });
      else if (["chart", "source", "side", "measure", "unit"].includes(name)) this.$emit("update-config", { [name]: value });
    },
    /** Una operación cuenta una vez aunque tenga el producto en varias unidades. */
    distinct(rows) {
      const seen = new Set();
      return rows.filter((o) => !seen.has(o.id + o.kind) && seen.add(o.id + o.kind));
    },
    qtyIn(o, unit) {
      return o.lines.filter((l) => (l.unit || "") === unit).reduce((s, l) => s + l.qty, 0);
    },
    amountOf(o) {
      const withAmount = o.lines.filter((l) => l.amount != null);
      return withAmount.length ? withAmount.reduce((s, l) => s + l.amount, 0) : null;
    },
    unitLabel(id) {
      return (id && this.data && this.data.units && this.data.units[id]) || this.t("noUnit");
    },
    qtyText(o) {
      return o.lines.map((l) => `${this.money(l.qty)} ${this.unitLabel(l.unit)}`).join(" + ");
    },
    partiesText(o) {
      if (o.buyer && o.seller) return `${this.contactName(o.seller)} → ${this.contactName(o.buyer)}`;
      if (o.buyer) return this.contactName(o.buyer);
      return o.seller ? this.contactName(o.seller) : "—";
    },
    sourceLabel(key) {
      return key ? this.t(`sources.${key}`) : "";
    },
  },
};
</script>

<style scoped>
.ciw { display: flex; flex-direction: column; gap: 8px; height: 100%; min-height: 0; }
.ciw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; }
.ciw-kpi { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; border-radius: 8px; background: var(--dw-surface-2); min-width: 0; }
.ciw-kpi__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-kpi__value { font-size: 1.15rem; font-weight: 700; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-caption { font-size: 0.75rem; color: var(--dw-muted); }
.ciw-chart { flex: 1; min-height: 120px; }
.ciw-list { list-style: none; margin: 0; padding: 0; overflow-y: auto; min-height: 0; }
.ciw-row { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--dw-border); }
.ciw-row:last-child { border-bottom: 0; }
.ciw-row__date { font-size: 0.75rem; font-weight: 600; min-width: 58px; color: var(--dw-muted); }
.ciw-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.ciw-row__title { font-weight: 600; font-size: 0.84rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.ciw-row__title:hover { color: var(--bs-primary); }
.ciw-row__text { font-size: 0.74rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-row__amount { font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
.ciw-row__amount small { font-size: 0.66rem; color: var(--dw-muted); }
.ciw-note { margin: 0; font-size: 0.7rem; color: var(--dw-muted); }
</style>
